import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import { ArrowLeft, Clock } from "lucide-react";

// Render legacy markdown-lite OR TipTap-produced HTML
function renderContent(content) {
  if (!content) return null;
  const trimmed = content.trim();
  // Detect HTML — TipTap output starts with an HTML tag
  if (trimmed.startsWith("<")) {
    return <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: content }} />;
  }
  const paragraphs = content.split(/\n\n+/);
  return paragraphs.map((p, i) => {
    if (p.startsWith("## ")) return <h2 key={i} className="font-serif text-3xl mt-10 mb-4">{p.replace(/^##\s+/, "")}</h2>;
    if (p.startsWith("**") && p.endsWith("**")) return <h3 key={i} className="font-serif text-2xl mt-8 mb-3 text-terracotta">{p.replace(/\*\*/g, "")}</h3>;
    const parts = p.split(/\*\*(.+?)\*\*/g);
    return (
      <p key={i} className="text-lg leading-[1.75] text-charcoal/85 mb-5 font-light">
        {parts.map((x, j) => (j % 2 === 1 ? <strong key={j} className="font-semibold text-charcoal">{x}</strong> : x))}
      </p>
    );
  });
}

export default function GuideDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null); setNotFound(false);
    api.get(`/articles/${slug}`).then(({ data }) => setArticle(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="container-x py-28 text-center">
      <SEO title="Article not found" />
      <div className="font-serif text-4xl">Article not found.</div>
      <Link to="/guides" className="btn-ghost mt-6 inline-flex">Back to all guides</Link>
    </div>
  );
  if (!article) return <div className="container-x py-28 text-center text-charcoal/60">Loading…</div>;

  return (
    <article className="pb-24">
      <SEO
        title={article.title}
        description={article.excerpt}
        image={article.image}
        type="article"
        article={article}
      />
      <div className="container-x pt-8">
        <Link to="/guides" className="inline-flex items-center gap-2 text-sm text-charcoal/70 hover:text-terracotta" data-testid="back-to-guides">
          <ArrowLeft size={14} /> All guides
        </Link>
      </div>
      <header className="container-x pt-10 pb-10 max-w-3xl">
        <div className="overline text-terracotta">{article.category}</div>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[1.02] tracking-tight text-balance">{article.title}</h1>
        <p className="mt-6 text-xl text-charcoal/75 leading-relaxed">{article.excerpt}</p>
        <div className="mt-6 flex items-center gap-4 text-sm text-charcoal/60">
          <span>{article.author}</span>
          <span className="flex items-center gap-1"><Clock size={12} /> {article.read_minutes} min read</span>
        </div>
      </header>
      <div className="w-full aspect-[21/9] bg-stone overflow-hidden">
        <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
      </div>
      <div className="container-x mt-12 max-w-3xl editorial-body">
        {renderContent(article.content)}
      </div>
    </article>
  );
}
